import { LiveStatus, RecorderStatus } from '@core/types';
import {
    isExternalEncoder,
    isStreamValidationBlocking,
    PresenterStatus,
    resolveIngestType,
    setDeviceIds,
    useAppDispatch,
    useAppSelector
} from '@online-webinar/store';
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import { BroadcastStatusPanel, BroadcastStatusToggle } from '../..';

export const BroadcastStatus = () => {
    const [isPanelVisible, setIsPanelVisible] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [buttonHeight, setButtonHeight] = useState(0);
    const [hasIssues, setHasIssues] = useState(false);
    const [prevHasIssues, setPrevHasIssues] = useState(false);
    const [encoderBlocked, setEncoderBlocked] = useState(false);
    const [prevEncoderBlocked, setPrevEncoderBlocked] = useState(false);
    const [lastPanelOpenTime, setLastPanelOpenTime] = useState(0);
    const [lastEncoderPanelOpenTime, setLastEncoderPanelOpenTime] = useState(0);

    const dispatch = useAppDispatch();
    const isActivePresenter = useAppSelector((state) => state.liveWebinar.isActivePresenter);
    const presenterStatus = useAppSelector((state) => state.liveWebinar.presenterStatus);
    const audioDeviceId = useAppSelector((state) => state.liveWebinar.audioDeviceId);
    const videoDeviceId = useAppSelector((state) => state.liveWebinar.videoDeviceId);
    const broadcastState = useAppSelector((state) => state.liveWebinar.broadcastState);
    const recorderStatus = useAppSelector((state) => state.liveWebinar.recorderStatus);
    const audioScore = useAppSelector((state) => state.liveWebinar.audioScore);
    const videoScore = useAppSelector((state) => state.liveWebinar.videoScore);
    const dataStatus = useAppSelector((state) => state.liveWebinar.dataConnection.status);
    const hasEnded = useAppSelector((state) => state.liveWebinar.hasEnded);
    const failedServerActions = useAppSelector((state) => state.liveWebinar.failedServerActions);
    const streamValidation = useAppSelector((state) => state.liveWebinar.streamValidation);
    const moqPublisher = useAppSelector((state) => state.liveWebinar.moqPublisher);

    useEffect(() => {
        let recorderIssue = false;
        if (presenterStatus === PresenterStatus.Live && broadcastState === 'broadcasting' && recorderStatus !== RecorderStatus.Recording) {
            recorderIssue = true;
        }
        if (presenterStatus === PresenterStatus.Ready && broadcastState === 'broadcasting' && recorderStatus !== RecorderStatus.StandBy) {
            recorderIssue = true;
        }

        // Gated on the source, exactly like the disabled start button and the panel's explanatory
        // entry: the verdict comes from Vindral's edge output, which exists for a WebRTC broadcast
        // too, so it must only pop the panel open for a stream the presenter's own encoder produced.
        const isEncoderBlocked =
            isExternalEncoder(resolveIngestType(videoDeviceId, moqPublisher)) && isStreamValidationBlocking(streamValidation);
        setEncoderBlocked(isEncoderBlocked);

        setHasIssues(
            !videoDeviceId ||
                !audioDeviceId ||
                dataStatus !== LiveStatus.Live ||
                (!hasEnded && isActivePresenter && broadcastState !== 'broadcasting') ||
                (!hasEnded && isActivePresenter && broadcastState === 'broadcasting' && (audioScore || 0) + (videoScore || 0) !== 20) ||
                recorderIssue ||
                (!!failedServerActions && failedServerActions.length > 0) ||
                isEncoderBlocked
        );
    }, [
        videoDeviceId,
        audioDeviceId,
        dataStatus,
        broadcastState,
        audioScore,
        videoScore,
        recorderStatus,
        presenterStatus,
        hasEnded,
        failedServerActions,
        streamValidation,
        moqPublisher
    ]);

    useEffect(() => {
        if (!hasIssues) {
            setPrevHasIssues(hasIssues);
            return;
        }

        if (hasIssues === prevHasIssues) {
            return;
        }

        const timeout = setTimeout(() => {
            setPrevHasIssues(hasIssues);
        }, 1000);
        return () => clearTimeout(timeout);
    }, [hasIssues, prevHasIssues]);

    // `hasIssues` is already true for almost the entire pre-go-live window, for the unrelated
    // "not broadcasting yet" reason below - so it never re-edges false-to-true when the encoder
    // verdict resolves a few seconds later, and the effect below would never see it. Debouncing
    // `encoderBlocked` on its own gives the encoder fault its own false-to-true edge to pop on.
    useEffect(() => {
        if (!encoderBlocked) {
            setPrevEncoderBlocked(encoderBlocked);
            return;
        }

        if (encoderBlocked === prevEncoderBlocked) {
            return;
        }

        const timeout = setTimeout(() => {
            setPrevEncoderBlocked(encoderBlocked);
        }, 1000);
        return () => clearTimeout(timeout);
    }, [encoderBlocked, prevEncoderBlocked]);

    useEffect(() => {
        if (!prevHasIssues || !isActivePresenter || presenterStatus !== PresenterStatus.Live) {
            return;
        }

        const now = DateTime.now().toMillis();
        if (now - lastPanelOpenTime < 60000) {
            return;
        }

        setIsPanelVisible(true);
        setLastPanelOpenTime(now);
    }, [prevHasIssues, lastPanelOpenTime]);

    // Its own cooldown, separate from the one above: an unrelated issue (e.g. "not broadcasting
    // yet", true for the whole pre-go-live window) can have opened and started that cooldown
    // moments earlier, and this fault must not be silenced by a timer that has nothing to do with
    // it. Ready is included alongside Live because Ready is the only state the "Go live!" button
    // renders in (see WebinarButtonStart in BroadcastLiveWebinar) - an encoder-blocked issue can
    // only ever be seen there, never in Live, since a blocked encoder prevents reaching it.
    //
    // presenterStatus/isActivePresenter are deliberately in the dependency array (unlike the
    // effect above): the Recorder can resolve the verdict before BroadcastVideoPreview's polling
    // reports the Vindral channel live, which is what flips presenterStatus to Ready. Without these
    // as deps, a block detected while still Initializing would be missed forever - the effect only
    // reruns on prevEncoderBlocked/cooldown changes, and prevEncoderBlocked does not flip again.
    useEffect(() => {
        if (
            !prevEncoderBlocked ||
            !isActivePresenter ||
            (presenterStatus !== PresenterStatus.Live && presenterStatus !== PresenterStatus.Ready)
        ) {
            return;
        }

        const now = DateTime.now().toMillis();
        if (now - lastEncoderPanelOpenTime < 60000) {
            return;
        }

        setIsPanelVisible(true);
        setLastEncoderPanelOpenTime(now);
    }, [prevEncoderBlocked, lastEncoderPanelOpenTime, presenterStatus, isActivePresenter]);

    const buttonRef = useRef<HTMLButtonElement>(null);
    const statusPanelRef = useRef<HTMLDivElement>(null);

    const togglePanel = () => {
        if (isPanelVisible) {
            startCloseTransition();
        } else {
            setIsPanelVisible(true);
        }
    };

    const startCloseTransition = () => {
        setIsTransitioning(false);
    };

    const handleTransitionEnd = () => {
        if (!isTransitioning) {
            setIsPanelVisible(false);
        }
    };

    const closePanel = () => {
        startCloseTransition();
    };

    const clickOutsidePanel = (e: MouseEvent) => {
        if (statusPanelRef.current && !statusPanelRef.current.contains(e.target as Node)) {
            startCloseTransition();
        }
    };

    const handleEscapeKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && isPanelVisible) {
            startCloseTransition();
        }
    };

    const onSelectDevices = () => {
        dispatch(setDeviceIds({ audioDeviceId: '', videoDeviceId: '' }));
    };

    useEffect(() => {
        if (isPanelVisible) {
            setTimeout(() => setIsTransitioning(true), 10); // Trigger the CSS transition
            document.addEventListener('mousedown', clickOutsidePanel);
            document.addEventListener('keydown', handleEscapeKey);
        } else {
            document.removeEventListener('mousedown', clickOutsidePanel);
            document.removeEventListener('keydown', handleEscapeKey);
        }
        return () => {
            document.removeEventListener('mousedown', clickOutsidePanel);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isPanelVisible]);

    useEffect(() => {
        if (isPanelVisible && buttonRef.current) {
            const height = buttonRef.current.clientHeight || buttonRef.current.offsetHeight;
            setButtonHeight(height);
        }
    }, [isPanelVisible]);

    return (
        <div className="flex -my-1 px-1">
            <BroadcastStatusToggle ref={buttonRef} onClick={togglePanel} aria-expanded={isTransitioning} hasIssues={hasIssues} />
            <div
                ref={statusPanelRef}
                id="statusPanel"
                className="absolute right-2 w-96 max-w-[calc(100%-.5rem)] ps-2 origin-top-right transition duraton-300 z-50"
                style={{
                    display: isPanelVisible ? 'block' : 'none',
                    opacity: isTransitioning ? 1 : 0,
                    transform: isTransitioning ? 'scale(1)' : 'scale(.75)',
                    top: `${buttonHeight}px` // Positioning the panel below the toggle button
                }}
                onTransitionEnd={handleTransitionEnd}
                aria-hidden={!isTransitioning}
            >
                <BroadcastStatusPanel
                    onClose={closePanel}
                    onSelectDevices={onSelectDevices}
                    audioDevice={audioDeviceId || ''}
                    broadcastState={broadcastState || 'not_broadcasting'}
                    audioScore={audioScore || 0}
                    videoScore={videoScore || 0}
                    videoDevice={videoDeviceId || ''}
                    recorderStatus={recorderStatus}
                    dataStatus={dataStatus}
                    presenterStatus={presenterStatus || PresenterStatus.Error}
                />
            </div>
        </div>
    );
};
